/**
 * Which fields of an edit session differ from the baseline it started at.
 *
 * The point is that it returns *names*, not a boolean. "You have unsaved
 * changes" as a bare `true` cost a full investigation once already: a load that
 * had saved cleanly kept insisting it was dirty, and nothing on screen or in the
 * code could say which field thought so. A list of keys turns that into a
 * one-line answer, and lets a confirm dialog tell someone what they are about to
 * discard instead of asking them to guess.
 */

/** Per-field equality, for values where `Object.is` is the wrong question. */
export type FieldComparators<T> = Partial<Record<keyof T, (a: unknown, b: unknown) => boolean>>;

export interface DirtyOptions<T> {
  /**
   * Fields that never count as user edits — transient UI state that lives in the
   * same object as the real form values.
   */
  ignore?: (keyof T)[];
  comparators?: FieldComparators<T>;
}

/**
 * Compares every key present on either side.
 *
 * Reading keys from both objects rather than just the baseline matters: a field
 * that only appears once the user touches it would otherwise never register.
 * `Object.is` is the default, so `NaN` equals `NaN` and `0` differs from `-0` —
 * both of which are the useful answers for form values.
 */
export function dirtyFields<T extends object>(
  current: T,
  baseline: T,
  options: DirtyOptions<T> = {}
): (keyof T)[] {
  const ignore = options.ignore ?? [];
  const comparators: FieldComparators<T> = options.comparators ?? {};
  const ignored = new Set<PropertyKey>(ignore as PropertyKey[]);

  const keys = new Set<PropertyKey>([...Object.keys(current), ...Object.keys(baseline)]);

  const changed: (keyof T)[] = [];

  keys.forEach((key) => {
    if (ignored.has(key)) return;

    const field = key as keyof T;
    const compare = comparators[field];
    const a = current[field];
    const b = baseline[field];

    if (compare ? !compare(a, b) : !Object.is(a, b)) {
      changed.push(field);
    }
  });

  return changed;
}

export function isDirty<T extends object>(
  current: T,
  baseline: T,
  options: DirtyOptions<T> = {}
): boolean {
  return dirtyFields(current, baseline, options).length > 0;
}

/**
 * A human-readable list of what changed, for a confirm dialog.
 *
 * Unlabelled fields fall back to their key, so a new form field shows something
 * recognisable rather than silently vanishing from the message.
 */
export function describeDirtyFields<T extends object>(
  fields: (keyof T)[],
  labels: Partial<Record<keyof T, string>> = {}
): string {
  return fields.map((field) => labels[field] ?? String(field)).join(', ');
}
